import { FeatureFlagNames, useFeatureFlags } from "@/providers/featureFlags";
import { useSelectedOrganization } from "@/components/common/OrganizationSwitcher/hooks";

import { useCurrentUserQuery } from "@/features/auth/queries/useCurrentUserQuery";
import { User, UserRoleKey } from "@/features/system/types";

import { CUSTOMER_SUPPORT_ORG_NAME } from "../constants";

export const MANAGEABLE_USER_ROLES = ["USER", "MANAGER"] as const;
export type ManageableUserRoleKey = (typeof MANAGEABLE_USER_ROLES)[number];

const MANAGER_VISIBLE_ROLE_FILTERS: UserRoleKey[] = ["USER", "MANAGER"];
const ADMIN_VISIBLE_ROLE_FILTERS: UserRoleKey[] = ["USER", "MANAGER", "ADMIN"];

function isManageableUserRole(role: UserRoleKey): boolean {
  return MANAGEABLE_USER_ROLES.includes(role as ManageableUserRoleKey);
}

function getVisibleUserRoleFilterKeys(isAdminViewer: boolean): UserRoleKey[] {
  return isAdminViewer
    ? ADMIN_VISIBLE_ROLE_FILTERS
    : MANAGER_VISIBLE_ROLE_FILTERS;
}

export function useIsAdminUserViewer(): boolean {
  const { data: currentUserData } = useCurrentUserQuery();
  return currentUserData?.user.role === "ADMIN";
}

export function useDefaultUserRoleFilters() {
  const isAdminViewer = useIsAdminUserViewer();
  return getVisibleUserRoleFilterKeys(isAdminViewer);
}

export function useVisibleUserRoleFilterKeys() {
  const isAdminViewer = useIsAdminUserViewer();
  return getVisibleUserRoleFilterKeys(isAdminViewer);
}

/**
 * True when a manager must not use User Management in the currently selected org.
 * Managers may always manage users in their home org; child-org access requires
 * {@link FeatureFlagNames.EnableManagerAccessForChildOrgUserMgmt} (resolved server-side).
 */
export function isManagerBlockedFromChildOrgUserManagement({
  role,
  homeOrgId,
  selectedOrgId,
  isChildOrgAccessEnabled,
}: {
  role: UserRoleKey | "ADMIN" | undefined;
  homeOrgId: string | undefined;
  selectedOrgId: string | undefined;
  isChildOrgAccessEnabled: boolean;
}): boolean {
  if (role !== "MANAGER") {
    return false;
  }
  if (!homeOrgId || !selectedOrgId || selectedOrgId === homeOrgId) {
    return false;
  }
  return !isChildOrgAccessEnabled;
}

export function useIsManagerBlockedFromChildOrgUserManagement(): boolean {
  const { isEnabled } = useFeatureFlags();
  const { data: currentUserData } = useCurrentUserQuery();
  const [selectedOrg] = useSelectedOrganization();

  return isManagerBlockedFromChildOrgUserManagement({
    role: currentUserData?.user.role,
    homeOrgId: currentUserData?.user.orgId,
    selectedOrgId: selectedOrg?.orgId,
    isChildOrgAccessEnabled: isEnabled(
      FeatureFlagNames.EnableManagerAccessForChildOrgUserMgmt,
    ),
  });
}

export function useCanAccessUsersManagementPage(): boolean {
  const { isEnabled } = useFeatureFlags();
  const { data: currentUserData } = useCurrentUserQuery();
  const isBlockedInChildOrg = useIsManagerBlockedFromChildOrgUserManagement();

  if (!isEnabled(FeatureFlagNames.ShowUsersManagement)) {
    return false;
  }

  const role = currentUserData?.user.role;
  if (role !== "ADMIN" && role !== "MANAGER") {
    return false;
  }

  return !isBlockedInChildOrg;
}

export function useCanManageUsers(): boolean {
  const { isEnabled } = useFeatureFlags();
  const { data: currentUserData } = useCurrentUserQuery();
  const isBlockedInChildOrg = useIsManagerBlockedFromChildOrgUserManagement();

  const role = currentUserData?.user.role;
  if (role === undefined) {
    return false;
  }
  if (role === "MANAGER") {
    return !isBlockedInChildOrg;
  }
  if (role === "ADMIN") {
    return (
      isEnabled(FeatureFlagNames.ManageUserFromSummitForAdmins) ||
      currentUserData?.user.orgName == CUSTOMER_SUPPORT_ORG_NAME
    );
  }
  return false;
}

export function useCanManageUser(targetUser: User): boolean {
  const canManageUsers = useCanManageUsers();
  if (!canManageUsers) {
    return false;
  }
  return isManageableUserRole(targetUser.role);
}
